import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import api from '../../services/api';
import usePageTitle from '../../utils/usePageTitle';
import ProductQualitySupport from './ProductQualitySupport';
import './SummaryInvoiceDetails.css';

const SummaryInvoiceDetails = () => {
  const { projectCode } = useParams();
  usePageTitle(`Summary Invoice - ${projectCode}`);
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQualitySupport, setShowQualitySupport] = useState(false);
  const [formData, setFormData] = useState({
    manager: '',
    cc: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    const loadInvoice = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/summary-invoice/${projectCode}`);
        setInvoice(response.data);
        setFormData({
          manager: response.data.manager || '',
          cc: response.data.cc || ''
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching invoice details:', err);
        setError('Failed to load invoice details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadInvoice();
  }, [projectCode]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/summary-invoice/${projectCode}`);
      setInvoice(response.data);
      setFormData({
        manager: response.data.manager || '',
        cc: response.data.cc || ''
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      setError('Failed to load invoice details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    const confirmSubmit = window.confirm(
      invoice.projectStatus === 'INS' 
        ? 'Invoice has been already submitted. Do you want to send the details again to finance by email?'
        : 'Are you sure you want to submit this invoice?'
    );

    if (!confirmSubmit) return;

    try {
      await api.post('/summary-invoice/submit', {
        projectCode: invoice.projectCode,
        manager: formData.manager,
        cc: formData.cc,
        modifiedDate: invoice.modifiedDate,
        sendEmail: true
      });

      setSuccessMessage('Summary Invoice submitted successfully');
      setHasChanges(false);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setTimeout(() => {
        setSuccessMessage(null);
        fetchInvoiceDetails();
      }, 5000);
    } catch (err) {
      console.error('Error submitting invoice:', err);
      setError(err.response?.data?.error || 'Failed to submit invoice. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleClose = async () => {
    const confirmClose = window.confirm('Are you sure you want to mark this invoice as payment received?');
    if (!confirmClose) return;

    try {
      await api.put(`/summary-invoice/${projectCode}/close`);
      
      setSuccessMessage('Invoice closed successfully');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setTimeout(() => {
        setSuccessMessage(null);
        fetchInvoiceDetails();
      }, 5000);
    } catch (err) {
      console.error('Error closing invoice:', err);
      setError('Failed to close invoice. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    if (hasChanges) {
      const confirmLeave = window.confirm('Do you want to save the changes?');
      if (confirmLeave) {
        handleSubmit();
      }
    }
    navigate('/summary-invoice');
  };

  const getWorkTypeName = (workType) => {
    const workTypeMap = {
      'AUD': 'Audit',
      'CET': 'CET',
      'LAB': 'Labeling',
      'REW': 'Rework',
      'SCR': 'Screening',
      'SQV': 'SQV',
      'UPG': 'Upgrade',
      'CFG': 'Configuration',
      'RBX': 'Rebox',
      'BXR': 'Rbox',
      'C55': 'CD55',
      'PZG': 'Palletizing',
      'DFG': 'Deconfiguration',
      'STG': 'Sorting',
      'PCI': 'Power Cord Insert',
      'BDG': 'Bundling'
    };
    return workTypeMap[workType] || workType;
  };

  const isEditable = invoice?.projectStatus === 'APP' || invoice?.projectStatus === 'INS';

  if (loading) {
    return (
      <div className="invoice-details-container" role="main" aria-busy="true">
        <div className="loading-spinner" aria-live="polite">
          <span className="sr-only">Loading invoice details...</span>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-details-container" role="main">
        <div className="error-message" role="alert">
          {error}
        </div>
        <button onClick={() => navigate('/summary-invoice')} className="btn-secondary">
          Back to List
        </button>
      </div>
    );
  }

  if (showQualitySupport) {
    return <ProductQualitySupport projectCode={projectCode} onBack={() => setShowQualitySupport(false)} />;
  }

  return (
    <div className="invoice-details-container" role="main">
      {error && (
        <Alert severity="error" role="alert" aria-live="assertive" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" role="status" aria-live="polite" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      
      <header className="invoice-header">
        <h1 className="invoice-title">SUMMARY INVOICE</h1>
        <h2 className="invoice-subtitle">Sony Electronics Inc.</h2>
      </header>

      <div className="project-code-section">
        <strong>Project Code: </strong>
        <button 
          className="link-button"
          onClick={() => setShowQualitySupport(true)}
          aria-label={`View product quality support for project ${invoice.code}`}
        >
          {invoice.code}
        </button>
      </div>

      <div className="invoice-content">
        <div className="invoice-grid">
          <section className="billing-section" aria-labelledby="billing-heading">
            <h3 id="billing-heading" className="sr-only">Billing Information</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <th scope="row">Billed To:</th>
                  <td>{invoice.custName}</td>
                </tr>
                {invoice.address1 && (
                  <tr>
                    <td></td>
                    <td>{invoice.address1}</td>
                  </tr>
                )}
                {invoice.address2 && (
                  <tr>
                    <td></td>
                    <td>{invoice.address2}</td>
                  </tr>
                )}
                {invoice.address3 && (
                  <tr>
                    <td></td>
                    <td>{invoice.address3}</td>
                  </tr>
                )}
                <tr>
                  <th scope="row">Attn:</th>
                  <td>{invoice.reqName} - {invoice.picName}</td>
                </tr>
                <tr>
                  <th scope="row">Finance:</th>
                  <td>
                    {isEditable ? (
                      <input
                        type="text"
                        name="manager"
                        value={formData.manager}
                        onChange={handleInputChange}
                        maxLength="500"
                        className="form-input"
                        aria-label="Finance email addresses"
                      />
                    ) : (
                      invoice.manager
                    )}
                  </td>
                </tr>
                <tr>
                  <th scope="row">CC:</th>
                  <td>
                    {isEditable ? (
                      <input
                        type="text"
                        name="cc"
                        value={formData.cc}
                        onChange={handleInputChange}
                        maxLength="500"
                        className="form-input"
                        aria-label="CC email addresses"
                      />
                    ) : (
                      invoice.cc
                    )}
                  </td>
                </tr>
                <tr>
                  <td colSpan="2">
                    <em>Note: Separate multiple email addresses with semicolon (;)</em>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="invoice-info-section" aria-labelledby="invoice-info-heading">
            <h3 id="invoice-info-heading" className="sr-only">Invoice Information</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <th scope="row">Billing Date:</th>
                  <td>{invoice.billingDate}</td>
                </tr>
                <tr>
                  <th scope="row">Business Group:</th>
                  <td>{invoice.businessGroup}</td>
                </tr>
                <tr>
                  <th scope="row">Debit Account:</th>
                  <td>{invoice.debitAcct}</td>
                </tr>
                <tr>
                  <th scope="row">Credit Account:</th>
                  <td>{invoice.creditAcct}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <section className="work-info-section" aria-labelledby="work-info-heading">
          <h3 id="work-info-heading" className="sr-only">Work Information</h3>
          <p><strong>Type of Work:</strong> {getWorkTypeName(invoice.workType)}</p>
          <p><strong>Work Performed:</strong> {invoice.workPerformed}</p>
        </section>

        <section className="details-section" aria-labelledby="details-heading">
          <h3 id="details-heading" className="sr-only">Invoice Details</h3>
          <table className="details-table" aria-label="Invoice line items and totals">
            <thead>
              <tr>
                <th scope="col">Model Code</th>
                <th scope="col">Quantity</th>
                <th scope="col">Unit Cost</th>
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.detailsList?.map((detail, index) => (
                <tr key={detail.id || index} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                  <td>{detail.code}</td>
                  <td className="text-center">{detail.quantity}</td>
                  <td className="text-right">${detail.unitCost}</td>
                  <td className="text-right">${detail.invoiceTotal}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan="2"></td>
                <td className="text-right"><strong>Grand Total:</strong></td>
                <td className="text-right"><strong>${invoice.cumulativeTotalInvoice}</strong></td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="approval-section" aria-labelledby="approval-heading">
          <h3 id="approval-heading" className="sr-only">Approval Information</h3>
          <div className="approval-info">
            <p><strong>Approved By:</strong> {invoice.approvedBy}</p>
            <p><strong>Approved Date:</strong> {invoice.approvedDate}</p>
          </div>
        </section>
      </div>

      <div className="action-bar no-print">
        {isEditable && (
          <>
            <button onClick={handleSubmit} className="btn-primary" aria-label="Submit invoice">
              Submit
            </button>
            {invoice.projectStatus === 'INS' && (
              <button onClick={handleClose} className="btn-warning" aria-label="Mark payment received">
                Payment Received
              </button>
            )}
          </>
        )}
        <button onClick={handlePrint} className="btn-secondary" aria-label="Print invoice">
          Print
        </button>
        <button onClick={handleBack} className="btn-secondary" aria-label="Go back">
          Back
        </button>
      </div>
    </div>
  );
};

export default SummaryInvoiceDetails;
